import { Box, Chip, Option } from "@powerledger/ui-component-lib";
import { kebabCase } from "lodash";
import { useTranslation } from "react-i18next";
import { MultiValue, SingleValue } from "react-select";

import { LocationChips } from "@/app/components/location-chips/location-chips";
import { FiltersType, SelectTypeOnChange, UseFilterReturn } from "@/app/components/page-header/filter/filter.types";
import { getSelectTranslation } from "@/app/lib/get-translations-for-components";
import { AggregatedRegistryCode } from "@/app/types/okta.types";

import { ErrorMessage } from "../form";
import { FormFieldLabel } from "../form/form-field-label";
import { getOptionsLengthForCss } from "../local-selects/helpers";
import FilterDropdown from "../page-header/filter/filter-dropdown";

export const RecFormFilter = ({
  filter,
  stackedValues,
  onChange,
  onBlur,
  removeValue,
  disabled,
  registryCode,
}: {
  disabled?: boolean;
  filter: FiltersType[0];
  registryCode?: AggregatedRegistryCode;
  onChange?: (option: SingleValue<Option> | MultiValue<Option>) => void;
  onBlur?: () => void;
  removeValue: UseFilterReturn["removeValue"];
  stackedValues?: UseFilterReturn["stackedValues"];
}) => {
  const { t } = useTranslation();
  const {
    name,
    options,
    values,
    styles,
    disabled: filterDisabled,
    components,
    isMulti,
    disableOptions,
    onChange: onFilterChange,
  } = filter;
  const currentFilterSelectedValues = stackedValues?.filter((item) => filter.keys?.includes(item.type));
  const hasErrorMessage = filter.isRequired && !currentFilterSelectedValues?.length;
  const fieldName = name.replace(/\s/g, "-").toLowerCase();
  const selectValues = Array.isArray(values)
    ? options[0]?.options
      ? options.flatMap((o) => o.options?.filter((so) => values?.includes(so.value)) ?? [])
      : options.filter((o) => values.includes(o.value))
    : options.find((opt) => opt.value === values);

  const handleChange: SelectTypeOnChange<Option, typeof isMulti> = (value) => {
    onChange?.(value);
    onFilterChange?.(value);
  };

  const defaultMenuHeight = 358;
  const menuHeightForLessOptions = 128 + getOptionsLengthForCss(options) * 37; // 37px is the height of each option, 165 (a suitable height with search and margins) - 37 = 128
  const menuHeight = menuHeightForLessOptions > defaultMenuHeight ? defaultMenuHeight : menuHeightForLessOptions;

  return (
    <Box sx={{ mb: 3 }}>
      <FormFieldLabel
        small
        hasErrorMessage={false}
        label={filter.label ?? t(name)}
        info={filter.info}
        name={fieldName}
        sx={{ height: 24, mb: 1 }}
      />
      <FilterDropdown
        button={{
          type: "button", // Otherwise acts like a submit button and it's impact can be seen if used inside a form
        }}
        placeholder={currentFilterSelectedValues?.length ? t("Select") : t("Any")}
        options={options}
        inputStyles={styles}
        disabled={disabled || filterDisabled}
        disableOptions={disableOptions}
        value={selectValues}
        onChange={handleChange}
        translation={getSelectTranslation(t)}
        isMulti={isMulti}
        components={components}
        wrapperStyles={{
          containerStyles: {
            "> button": {
              backgroundColor: "pageBackground",
              height: 44,
              width: "100%",
              fontWeight: "normal",
            },
            "> div": {
              width: "100%",
            },
          },
          menuStyles: {
            height: menuHeight,
          },
        }}
        onMenuClose={onBlur}
        testId={`${kebabCase(name)}-form`}
      />
      {fieldName && hasErrorMessage && <ErrorMessage name={fieldName} sx={{ textAlign: "left", mt: 3 }} />}

      {currentFilterSelectedValues?.length ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mt: 3,
          }}
          data-testid={kebabCase(`${name}-selected-values`)}
        >
          {filter.keys?.includes("locations") ? (
            <LocationChips
              locations={currentFilterSelectedValues}
              removeValue={!filter.disabled ? removeValue : undefined}
              registryCode={registryCode}
            />
          ) : (
            currentFilterSelectedValues?.map((item) => (
              <Box key={item.value} data-testid={item.label}>
                <Chip
                  key={item.value}
                  label={item.label}
                  onRemove={
                    !filter.disabled
                      ? () => {
                          removeValue(item.type, item.value);

                          if (onChange) {
                            const _currentFilterSelectedValues = currentFilterSelectedValues.filter(
                              (f) => f.value !== item.value,
                            );
                            onChange?.(
                              _currentFilterSelectedValues.map((filterValue) => ({
                                value: filterValue.value,
                                label: filterValue.label,
                              })),
                            );
                          }
                        }
                      : undefined
                  }
                />
              </Box>
            ))
          )}
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};
